import React, { useMemo, useState, useEffect } from "react";
import config from "../../config";
import SuccessPopup from "../../components/common/SuccessPopup";
import ErrorPopup from "../../components/common/ErrorPopup";

const defaultRoles = [

];

const AddInvoiceModal = ({ open, onClose, onGenerate, isSubVendor }) => {
  const [clientKey, setClientKey] = useState("");
  const [workSite, setWorkSite] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [payrollMonth, setPayrollMonth] = useState("");
  const [settlementMonth, setSettlementMonth] = useState("");
  const [noOfEmployees, setNoOfEmployees] = useState("");
  const [roles, setRoles] = useState(defaultRoles);
  const [clients, setClients] = useState([]);
  const [workSites, setWorkSites] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingWorkSites, setLoadingWorkSites] = useState(false);
  const [subVendors, setSubVendors] = useState([]);
  const [loadingSubVendors, setLoadingSubVendors] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    setErrors({});
  }, [open, isSubVendor]);

  useEffect(() => {
    if (roles.length > 0) {
      setErrors((prev) => ({ ...prev, roles: "" }));
    }
  }, [roles]);

  const clientLabel = useMemo(
    () => (isSubVendor ? "Sub Vendor Name / Key" : "Client Name / Key"),
    [isSubVendor]
  );

  // Fetch clients when modal opens (for client tab)
  useEffect(() => {
    if (!open || isSubVendor) return;

    const fetchClients = async () => {
      setLoadingClients(true);
      try {
        const orgId = localStorage.getItem("orgId") || "";
        const token = localStorage.getItem("Authorization") || "";

        const response = await fetch(
          `${config.API_BASE_URL}${config.CLIENT_LIST_ENDPOINT}${orgId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch clients");
        }

        const data = await response.json();
        const clientList = Array.isArray(data) ? data : data.body || [];
        setClients(clientList);
      } catch (error) {
        console.error("Error fetching clients:", error);
        setClients([]);
      } finally {
        setLoadingClients(false);
      }
    };

    fetchClients();
  }, [open, isSubVendor]);

  // Fetch sub vendors when modal opens (for sub vendor tab)
  useEffect(() => {
    if (!open || !isSubVendor) return;

    const fetchSubVendors = async () => {
      setLoadingSubVendors(true);
      try {
        const orgId = localStorage.getItem("orgId") || "";
        const token = localStorage.getItem("Authorization") || "";

        const response = await fetch(
          `${config.API_BASE_URL}${config.SUBVENDOR_LIST_ENDPOINT}?orgKey=${orgId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch sub-vendors");
        }

        const data = await response.json();
        const vendorList = Array.isArray(data) ? data : data.body || [];
        setSubVendors(vendorList);
      } catch (error) {
        console.error("Error fetching sub-vendors:", error);
        setSubVendors([]);
      } finally {
        setLoadingSubVendors(false);
      }
    };

    fetchSubVendors();
  }, [open, isSubVendor]);

  // Fetch work sites when a client is selected
  useEffect(() => {
    if (!clientKey || isSubVendor) {
      setWorkSites([]);
      return;
    }

    const fetchWorkSites = async () => {
      setLoadingWorkSites(true);
      try {
        const orgId = localStorage.getItem("orgId") || "";
        const token = localStorage.getItem("Authorization") || "";

        const response = await fetch(
          `${config.API_BASE_URL}${config.WORK_SITE_ENDPOINT}?orgKey=${orgId}&clientId=${clientKey}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch work sites");
        }

        const data = await response.json();
        const siteList = data.body || [];
        setWorkSites(siteList);
      } catch (error) {
        console.error("Error fetching work sites:", error);
        setWorkSites([]);
      } finally {
        setLoadingWorkSites(false);
      }
    };

    fetchWorkSites();
  }, [clientKey, isSubVendor]);

  // Fetch job roles when both client and worksite are selected
  useEffect(() => {
    if (!clientKey || !workSite || isSubVendor) {
      setRoles(defaultRoles);
      return;
    }

    const fetchJobRoles = async () => {
      setLoadingRoles(true);
      try {
        const orgId = localStorage.getItem("orgId") || "";
        const token = localStorage.getItem("Authorization") || "";

        const response = await fetch(
          `${config.API_BASE_URL}${config.JOB_ROLE_ENDPOINT}/${orgId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch job roles");
        }

        const data = await response.json();
        const roleList = Array.isArray(data) ? data : data.body || [];
        setRoles(roleList);
      } catch (error) {
        console.error("Error fetching job roles:", error);
        setRoles(defaultRoles);
      } finally {
        setLoadingRoles(false);
      }
    };

    fetchJobRoles();
  }, [clientKey, workSite, isSubVendor]);

  if (!open) return null;

  const removeRole = (r) => {
    setRoles((prev) =>
      prev.filter((x) => {
        if (typeof x === 'string' && typeof r === 'string') {
          return x !== r;
        } else if (typeof x === 'object' && typeof r === 'object') {
          return x.id !== r.id;
        } else {
          return true;
        }
      })
    );
  };

  const handleGenerate = async () => {
    const newErrors = {};
    const orgKey = localStorage.getItem("orgId") || "0";
    const token = localStorage.getItem("Authorization");

    if (isSubVendor) {
      if (!clientKey) {
        newErrors.clientKey = "Sub Vendor is required.";
      }
      if (!invoiceDate) {
        newErrors.invoiceDate = "Invoice Date is required.";
      }
      if (!dueDate) {
        newErrors.dueDate = "Due Date is required.";
      }
      if (!settlementMonth) {
        newErrors.settlementMonth = "Settlement Month is required.";
      }
      if (!noOfEmployees || Number(noOfEmployees) <= 0) {
        newErrors.noOfEmployees = "Number of Employees is required and must be greater than 0.";
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      try {
        // Find the selected vendor object to get the Name
        const selectedVendor = subVendors.find(
          (v) => String(v.subVendorId || v.id) === String(clientKey)
        );

        const formattedInvoiceDate = invoiceDate ? `${invoiceDate}T00:00:00` : "";
        const formattedDueDate = dueDate ? `${dueDate}T00:00:00` : "";
        const formattedSettlementMonth =
          settlementMonth && invoiceDate
            ? `${settlementMonth.substring(0, 3).toUpperCase()}-${new Date(invoiceDate).getFullYear()}`
            : "";

        // Construct the exact payload you requested
        const payload = {
          orgKey: orgKey, // Sent as string "32" per your request
          subVendorKey: Number(clientKey),
          subVendorName: selectedVendor?.subVendorName || selectedVendor?.name || "",
          invoiceDate: formattedInvoiceDate,
          dueDate: formattedDueDate,
          settlementMonth: formattedSettlementMonth,
          noOfEmployees: Number(noOfEmployees),
          invoiceAmount: 0.0, // Backend typically calculates this, or add a state for it
          workSite: "N/A",    // Default or add a field if needed
        };

        const response = await fetch(
          `${config.API_BASE_URL}${config.SUBVENDOR_INVOICE_GENERATE_ENDPOINT}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();
        setSuccessMessage("Sub Vendor Invoice generated successfully!");
        onGenerate?.(data);
      } catch (error) {
        console.error("Error generating invoice:", error);
        setErrorMessage("Failed to generate invoice. Please try again.");
      }
      return;
    }

    // --- Logic for Client Invoice (Non-SubVendor) remains here ---
    if (!clientKey) {
      newErrors.clientKey = "Client is required.";
    }
    if (!workSite) {
      newErrors.workSite = "Work Site is required.";
    }
    if (!invoiceDate) {
      newErrors.invoiceDate = "Invoice Date is required.";
    }
    if (!dueDate) {
      newErrors.dueDate = "Due Date is required.";
    }
    if (!payrollMonth) {
      newErrors.payrollMonth = "Payroll Month is required.";
    }
    if (roles.length === 0) {
      newErrors.roles = "At least one Job Role is required.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const formattedInvoiceDate = invoiceDate ? `${invoiceDate}T00:00:00` : "";
      const formattedDueDate = dueDate ? `${dueDate}T00:00:00` : "";
      const jobRoles = roles.map((r) => (typeof r === "string" ? r : r.name));
      const formattedPayrollMonth =
        payrollMonth && invoiceDate
          ? `${payrollMonth.substring(0, 3).toUpperCase()}-${new Date(invoiceDate).getFullYear()}`
          : "";

      const clientPayload = {
        orgKey: Number(orgKey),
        clientKey: Number(clientKey),
        workSite,
        invoiceDate: formattedInvoiceDate,
        dueDate: formattedDueDate,
        payrollMonth: formattedPayrollMonth,
        jobRoles,
      };

      const clientResponse = await fetch(
        `${config.API_BASE_URL}${config.CLIENT_INVOICE_GENERATE_ENDPOINT}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(clientPayload),
        }
      );

      if (!clientResponse.ok) throw new Error("Client API Error");

      const clientData = await clientResponse.json();
      setSuccessMessage("Invoice generated successfully!");
      onGenerate?.(clientData);

    } catch (error) {
      console.error("Error generating invoice:", error);
      setErrorMessage("Failed to generate invoice. Please try again.");
    }
  };


  return (
    <div className="add-invoice-overlay" role="dialog" aria-modal>
      <div className="add-invoice-card">
        <div className="add-invoice-header">
          <h3 className="add-invoice-title">Add Invoice</h3>
        </div>

        <div className="add-invoice-form">
          <div className="field">
            <label>{clientLabel}*</label>
            <select
              value={clientKey}
              onChange={(e) => {
                setClientKey(e.target.value);
                setErrors((prev) => ({ ...prev, clientKey: "" }));
              }}
              className={`input ${errors.clientKey ? "input-error" : ""}`}
              disabled={isSubVendor ? loadingSubVendors : loadingClients}
            >
              <option value="" disabled>
                {isSubVendor
                  ? (loadingSubVendors ? "Loading sub vendors..." : "Select a sub vendor")
                  : (loadingClients ? "Loading clients..." : "Select a client")
                }
              </option>
              {isSubVendor
                ? subVendors.map((vendor) => (
                  <option key={vendor.subVendorId || vendor.id} value={vendor.subVendorId || vendor.id}>
                    {vendor.subVendorName || vendor.name}
                  </option>
                ))
                : clients.map((client) => (
                  <option key={client.id || client.clientKey} value={client.clientKey || client.id}>
                    {client.name || client.clientName}
                  </option>
                ))
              }
            </select>
            {errors.clientKey && (
              <span className="error-text">
                {errors.clientKey}
              </span>
            )}
          </div>

          {/* Work Site field - only for Client */}
          {!isSubVendor && (
            <div className="field">
              <label>Work Site*</label>
              <select
                value={workSite}
                onChange={(e) => {
                  setWorkSite(e.target.value);
                  setErrors((prev) => ({ ...prev, workSite: "" }));
                }}
                className={`input ${errors.workSite ? "input-error" : ""}`}
                disabled={loadingWorkSites || !clientKey}
              >
                <option value="" disabled>
                  {!clientKey ? "Select a client first" : loadingWorkSites ? "Loading work sites..." : "Select a work site"}
                </option>
                {workSites.map((site) => (
                  <option key={site} value={site}>
                    {site}
                  </option>
                ))}
              </select>
              {errors.workSite && (
                <span className="error-text">
                  {errors.workSite}
                </span>
              )}
            </div>
          )}

          <div className="field">
            <label>Invoice Date*</label>
            <input
              type="date"
              className={`input ${errors.invoiceDate ? "input-error" : ""}`}
              value={invoiceDate}
              onChange={(e) => {
                setInvoiceDate(e.target.value);
                setErrors((prev) => ({ ...prev, invoiceDate: "" }));
              }}
            />
            {errors.invoiceDate && (
              <span className="error-text">
                {errors.invoiceDate}
              </span>
            )}
          </div>

          <div className="field">
            <label>Due Date*</label>
            <input
              type="date"
              className={`input ${errors.dueDate ? "input-error" : ""}`}
              value={dueDate}
              onChange={(e) => {
                setDueDate(e.target.value);
                setErrors((prev) => ({ ...prev, dueDate: "" }));
              }}
            />
            {errors.dueDate && (
              <span className="error-text">
                {errors.dueDate}
              </span>
            )}
          </div>

          {/* Payroll Month - only for Client */}
          {!isSubVendor && (
            <div className="field">
              <label>Payroll Month*</label>
              <select
                value={payrollMonth}
                onChange={(e) => {
                  setPayrollMonth(e.target.value);
                  setErrors((prev) => ({ ...prev, payrollMonth: "" }));
                }}
                className={`input ${errors.payrollMonth ? "input-error" : ""}`}
              >
                <option value="" disabled>
                </option>
                {[
                  "January",
                  "February",
                  "March",
                  "April",
                  "May",
                  "June",
                  "July",
                  "August",
                  "September",
                  "October",
                  "November",
                  "December",
                ].map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              {errors.payrollMonth && (
                <span className="error-text">
                  {errors.payrollMonth}
                </span>
              )}
            </div>
          )}

          {/* Settlement Month - only for Sub Vendor */}
          {isSubVendor && (
            <div className="field">
              <label>Settlement Month*</label>
              <select
                value={settlementMonth}
                onChange={(e) => {
                  setSettlementMonth(e.target.value);
                  setErrors((prev) => ({ ...prev, settlementMonth: "" }));
                }}
                className={`input ${errors.settlementMonth ? "input-error" : ""}`}
              >
                <option value="" disabled>
                </option>
                {[
                  "January",
                  "February",
                  "March",
                  "April",
                  "May",
                  "June",
                  "July",
                  "August",
                  "September",
                  "October",
                  "November",
                  "December",
                ].map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              {errors.settlementMonth && (
                <span className="error-text">
                  {errors.settlementMonth}
                </span>
              )}
            </div>
          )}

          {/* No.of Employees - only for Sub Vendor */}
          {isSubVendor && (
            <div className="field">
              <label>No.of Employees*</label>
              <input
                type="number"
                className={`input ${errors.noOfEmployees ? "input-error" : ""}`}
                value={noOfEmployees}
                onChange={(e) => {
                  setNoOfEmployees(e.target.value);
                  setErrors((prev) => ({ ...prev, noOfEmployees: "" }));
                }}
                placeholder="Enter number of employees"
              />
              {errors.noOfEmployees && (
                <span className="error-text">
                  {errors.noOfEmployees}
                </span>
              )}
            </div>
          )}

          {/* Job Roles - only for Client */}
          {!isSubVendor && (
            <div className="field field-full">
              <label>Job Roles*</label>
              <div className={`roles-box ${errors.roles ? "input-error" : ""}`}>
                {roles.map((r) => {
                  const roleName = typeof r === 'string' ? r : r.name;
                  const roleId = typeof r === 'string' ? r : r.id;
                  return (
                    <span key={roleId} className="role-pill">
                      {roleName}
                      <button
                        type="button"
                        className="role-remove"
                        onClick={() => removeRole(r)}
                        aria-label={`Remove ${roleName}`}
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
              {errors.roles && (
                <span className="error-text">
                  {errors.roles}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="add-invoice-footer">
          <button type="button" className="btn btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-generate"
            onClick={handleGenerate}
          >
            Generate
          </button>
        </div>
      </div>

      {successMessage && (
        <SuccessPopup
          message={successMessage}
          onClose={() => {
            setSuccessMessage("");
            onClose();
          }}
        />
      )}
      {errorMessage && (
        <ErrorPopup
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}
    </div>
  );
};

export default AddInvoiceModal;
